import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios';
import { showToast } from '../components/ui/toast';

const API_ROUTES = {
  GET_SERVICES: '/admin/service',
  ADD_CATEGORY: '/admin/service',
  UPDATE_PROVIDER: '/admin/service',
  ADD_PROVIDERS: '/admin/service',
  REMOVE_PROVIDERS: '/admin/service',
  UPDATE_CATEGORY: '/admin/service',
  DELETE_CATEGORY: '/admin/service',
  UPDATE_SERVICE: '/admin/service'
};

// Get all services grouped by category
export const useGetServicesQuery = () => {
  const { data, isError, error, isLoading, refetch } = useQuery({
    queryKey: [API_ROUTES.GET_SERVICES],
    queryFn: () => api.get(API_ROUTES.GET_SERVICES),
  });

  return { 
    data: data?.data?.data || [], 
    isError, 
    error, 
    isLoading, 
    refetch 
  };
};

// Add new category with providers
export const useAddCategoryMutation = () => {
  const queryClient = useQueryClient();
  
  const { mutate, isError, error, isLoading, isSuccess, isPending } = useMutation({
    mutationFn: (data) => api.post(API_ROUTES.ADD_CATEGORY, data),
    onSuccess: () => {
      showToast('success', 'Category added successfully');
      queryClient.invalidateQueries([API_ROUTES.GET_SERVICES]);
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to add category');
    }
  });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Update provider details
export const useUpdateProviderMutation = () => {
  const queryClient = useQueryClient();
  
  const { mutate, isError, error, isLoading, isSuccess, isPending } = useMutation({
    mutationFn: ({ category, providerId, data }) => 
      api.put(`${API_ROUTES.UPDATE_PROVIDER}/${category}/providers/${providerId}`, data),
    onSuccess: () => {
      showToast('success', 'Provider updated successfully');
      queryClient.invalidateQueries([API_ROUTES.GET_SERVICES]);
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to update provider');
    }
  });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Add providers to existing category (duplicate-safe)
export const useAddProvidersMutation = () => {
  const queryClient = useQueryClient();
  
  const { mutate, isError, error, isLoading, isSuccess, isPending } = useMutation({
    mutationFn: ({ category, providers }) => 
      api.post(`${API_ROUTES.ADD_PROVIDERS}/${category}/providers`, { providers }),
    onSuccess: () => {
      showToast('success', 'Providers added successfully');
      queryClient.invalidateQueries([API_ROUTES.GET_SERVICES]);
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to add providers');
    }
  });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Remove providers from category
export const useRemoveProvidersMutation = () => {
  const queryClient = useQueryClient();
  
  const { mutate, isError, error, isLoading, isSuccess, isPending } = useMutation({
    mutationFn: ({ category, providers }) => 
      api.post(`${API_ROUTES.REMOVE_PROVIDERS}/${category}/providers`, { 
        remove: true, 
        providers 
      }),
    onSuccess: () => {
      showToast('success', 'Providers removed successfully');
      queryClient.invalidateQueries([API_ROUTES.GET_SERVICES]);
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to remove providers');
    }
  });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Update category name
export const useUpdateCategoryMutation = () => {
  const queryClient = useQueryClient();
  
  const { mutate, isError, error, isLoading, isSuccess, isPending } = useMutation({
    mutationFn: ({ oldCategory, newCategory }) => 
      api.put(`${API_ROUTES.UPDATE_CATEGORY}/${oldCategory}`, { newCategory }),
    onSuccess: () => {
      showToast('success', 'Category updated successfully');
      queryClient.invalidateQueries([API_ROUTES.GET_SERVICES]);
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to update category');
    }
  });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

// Delete category
export const useDeleteCategoryMutation = () => {
  const queryClient = useQueryClient();
  
  const { mutate, isError, error, isLoading, isSuccess, isPending } = useMutation({
    mutationFn: ({ category }) => 
      api.delete(`${API_ROUTES.DELETE_CATEGORY}/${category}`),
    onSuccess: () => {
      showToast('success', 'Category deleted successfully');
      queryClient.invalidateQueries([API_ROUTES.GET_SERVICES]);
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to delete category');
    }
  });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useUpdateServiceMutation = () => {
  const queryClient = useQueryClient();

  const { mutate, isError, error, isLoading, isSuccess, isPending } = useMutation({
    mutationFn: ({ id, category, providers }) =>
      api.put(`${API_ROUTES.UPDATE_SERVICE}/${id}`, { category, providers }),
    onSuccess: () => {
      showToast('success', 'Service updated successfully');
      queryClient.invalidateQueries([API_ROUTES.GET_SERVICES]);
    },
    onError: (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to update service');
    }
  });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};
